<template>
  <FeatureAnnouncementModal
    :is-open="isOpen"
    :announcements="mappedAnnouncements"
    @card-displayed="handleCardDisplayed"
    @cta="handleCta"
    @dismiss="handleDismiss"
    @closed="handleClosed"
  />
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { useFeatureAnnouncementsStore } from '@/stores/useFeatureAnnouncementsStore';
import { useWalkthroughLaunch } from '@/composables/useWalkthroughLaunch';

/**
 * The one modal for Feature Announcements, fed by the store's `modalAnnouncements`. Two things
 * open it, and the store decides which content wins:
 *
 * - **login** — the batch of unseen, unexpired announcements, pushed once per page load
 * - **the What's New panel** — one announcement the carrier clicked, which takes precedence
 *
 * Mounted app-wide (app.vue) rather than beside the panel, deliberately: on mobile the panel
 * lives inside the slide-out menu, and clicking a row closes that menu — anything rendered
 * from inside it would be unmounted before it could show.
 */
const store = useFeatureAnnouncementsStore();
const { modalAnnouncements, loginModalAnnouncements, requestedAnnouncementId } = storeToRefs(store);
const { launchWalkthrough } = useWalkthroughLaunch();
const { trackEvent, eventNames } = useAcvAnalytics();

/**
 * Which trigger is currently showing the modal — the login batch, or a single announcement
 * requested from the What's New panel. Drives the `source` property on the card-viewed and
 * walkthrough-started events. Read at the moment each event fires rather than derived from
 * `requestedAnnouncementId` directly, since `close()` clears that id before the walkthrough
 * actually launches (see `handleCta`/`handleClosed` below).
 */
const activeSource = ref('modal');

/**
 * Dismissing (or launching a walkthrough from) the login batch ends it for the rest of this
 * page load, per the epic's "closes for the session, doesn't reopen" rule — deliberately
 * in-memory only, not persisted. A hard refresh re-runs eligibility, which is the "resurfaces
 * on next login or page refresh" half of that same rule. It intentionally does NOT reset on a
 * client-side route change; unseen content should not pop back up because the carrier clicked
 * a nav link.
 *
 * It gates only the login trigger. An announcement the carrier explicitly clicks in the panel
 * always opens, however many times they've dismissed the login batch already.
 */
const dismissedThisPageLoad = ref(false);

/**
 * `isOpen` deliberately isn't a computed tied to `modalAnnouncements.length`. Paging through
 * the batch marks each card seen as it's displayed, which shrinks that live count — by the
 * time the carrier reaches the last card it can already be zero, and a plain computed would
 * close the modal mid-batch. Both triggers below are edge-triggered instead, and closing only
 * ever happens via an explicit dismiss/cta.
 */
const isOpen = ref(false);

watch(
  loginModalAnnouncements,
  (announcements) => {
    if (!dismissedThisPageLoad.value && announcements.length > 0) {
      // Guarded on `!isOpen.value` so this fires once per open, not on every shrink of the
      // batch as cards get paged through and marked seen.
      if (!isOpen.value) {
        trackEvent(eventNames.FEATURE_ANNOUNCEMENT_MODAL_SHOWN, {
          announcement_ids: announcements.map((announcement) => announcement.id),
          announcement_names: announcements.map((announcement) => announcement.name),
        });
      }
      activeSource.value = 'modal';
      isOpen.value = true;
    }
  },
  { immediate: true },
);

watch(requestedAnnouncementId, (id) => {
  if (id) {
    activeSource.value = 'panel';
    isOpen.value = true;
  }
});

const mappedAnnouncements = computed(() =>
  modalAnnouncements.value.map((announcement) => ({
    id: announcement.id,
    name: announcement.name,
    description: announcement.description,
    image: announcement.image ?? '',
    imageAlt: announcement.image ? announcement.name : '',
    showCta: announcement.type === 'walkthrough',
    ctaText: announcement.ctaText,
  })),
);

const handleCardDisplayed = (id) => {
  // Card-viewed fires only on the unseen→seen transition, so it must be checked before
  // markSeen runs — markSeen itself is idempotent and gives no signal either way.
  if (!store.isSeen(id)) {
    trackEvent(eventNames.FEATURE_ANNOUNCEMENT_CARD_VIEWED, {
      announcement_id: id,
      announcement_name: store.getById(id)?.name,
      type: store.getById(id)?.type,
      source: activeSource.value,
    });
  }
  store.markSeen(id);
};

/**
 * Set when a CTA has been pressed but the modal hasn't finished leaving the screen. The
 * walkthrough waits for `closed`: its steps are chosen by what's actually visible, and this
 * modal's scrim covers the viewport until it's gone. Launching straight from the CTA only
 * schedules a re-render, so the walkthrough would measure everything against the scrim and end
 * on the tick it began.
 */
const pendingWalkthroughId = ref(null);

/**
 * `activeSource` at the moment the CTA was pressed. `close()` clears `requestedAnnouncementId`
 * before `handleClosed` runs, so `activeSource` can't be read fresh at launch time — it has to
 * be captured here instead.
 */
const pendingWalkthroughSource = ref(null);

const handleCta = (id) => {
  // markSeen isn't called here: the cta can only fire from the card currently displayed,
  // which already marked itself seen via card-displayed.
  pendingWalkthroughId.value = id;
  pendingWalkthroughSource.value = activeSource.value;
  close();
};

const handleDismiss = () => {
  close();
};

const handleClosed = () => {
  const id = pendingWalkthroughId.value;
  const source = pendingWalkthroughSource.value;
  pendingWalkthroughId.value = null;
  pendingWalkthroughSource.value = null;

  const announcement = id ? store.getById(id) : null;
  if (announcement?.type === 'walkthrough') {
    launchWalkthrough(announcement, { source });
  }
};

const close = () => {
  dismissedThisPageLoad.value = true;
  isOpen.value = false;
  // So the next open falls back to the login batch rather than re-showing this card.
  store.clearRequestedAnnouncement();
};
</script>
